import { DialogComponent } from '@theme/dialog';

const STORAGE_KEY = 'array:wishlist:v1';
const CARD_SELECTOR = '.bc-card,.ar-cell,.november-hub__product,.array-last-chance__product,.dh-cell,.gt-card,.su-card,.kb2-item';

/** Mobile navigation and saved products, stored only in this browser. */
class ArrayMobileNav extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    this.controller = new AbortController();
    this.cards = new Map();
    this.labels = JSON.parse(this.refs.labels.textContent);
    this.items = this.readSaved();
    const options = { signal: this.controller.signal };
    document.addEventListener('click', this.handleSave, { ...options, capture: true });
    document.addEventListener('cart:update', this.updateCart, options);
    window.addEventListener('storage', this.handleStorage, options);
    this.observer = new MutationObserver(this.queueScan);
    this.observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['href'] });
    this.scan();
    this.faceObserver = new MutationObserver(this.queueScan);
    document.querySelectorAll('.gt-panel').forEach(panel => this.faceObserver.observe(panel, { subtree: true, attributes: true, attributeFilter: ['class'] }));
  }

  disconnectedCallback() {
    if (this.refs.dialog?.open) this.closeDialog();
    this.controller?.abort();
    this.cartRequest?.abort();
    this.observer?.disconnect();
    this.faceObserver?.disconnect();
    cancelAnimationFrame(this.frame);
    for (const [card, button] of this.cards || []) {
      button.remove();
      card.classList.remove('array-wish-card');
    }
    super.disconnectedCallback();
  }

  readSaved() {
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(stored) ? stored.filter(item => item && typeof item.handle === 'string' && /^[a-z0-9-]+$/i.test(item.handle)).slice(0, 100) : [];
    } catch { return []; }
  }

  handleStorage = event => {
    if (event.key !== STORAGE_KEY) return;
    this.items = this.readSaved();
    this.sync();
    if (this.refs.dialog.open) this.renderList();
  };

  queueScan = () => {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => { this.frame = 0; this.scan(); });
  };

  scan() {
    for (const [card, button] of this.cards) {
      if (!card.isConnected) { button.remove(); this.cards.delete(card); }
    }
    const roots = [...document.querySelectorAll(CARD_SELECTOR)];
    const product = document.querySelector('[data-pdp-root]');
    if (product) roots.push(product);
    for (const card of roots) {
      if (this.cards.has(card) || card.matches('a') || !this.recordFor(card)) continue;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'array-wish-save';
      button.append(this.refs.heart.content.cloneNode(true));
      if (card === product) button.append(document.createElement('span'));
      (card === product ? card.querySelector('.pdp-head') : card)?.append(button);
      card.classList.add('array-wish-card');
      this.cards.set(card, button);
    }
    this.sync();
  }

  recordFor(card) {
    const isProduct = card.matches('[data-pdp-root]');
    const face = card.querySelector('.gt-face.is-on');
    const link = face || card.querySelector('a[href*="/products/"]');
    let url;
    try { url = new URL(isProduct ? location.href : link?.href, location.origin); } catch { return null; }
    const handle = url.pathname.match(/\/products\/([a-z0-9-]+)/i)?.[1];
    if (!handle || url.origin !== location.origin) return null;
    const variant = url.searchParams.get('variant');
    url.search = '';
    url.hash = '';
    if (variant && /^\d+$/.test(variant)) url.searchParams.set('variant', variant);
    const meta = card.querySelector('.gt-mface.is-on') || card;
    const title = (isProduct ? card.querySelector('.pdp-h')?.textContent : meta.querySelector('.bc-name,.bc-title,.ar-name,.november-hub__product-title,.array-last-chance__product-title,.dh-name,.gt-name,.su-name,.kb2-name,.november-hub__product-link>span:not(.array-savings-badge),.array-last-chance__product-link>span:not(.array-savings-badge)')?.textContent) || link?.getAttribute('aria-label') || card.dataset.title;
    const img = isProduct ? card.querySelector('[data-pdp-main]') : face?.querySelector('img') || card.querySelector('.kb2-shot,img');
    return { handle, url: url.pathname + url.search, title: (title || img?.alt || handle).trim(), image: img?.currentSrc || img?.src || '', priceText: meta.querySelector('[data-pdp-price],.bc-price,.ar-price,.november-hub__product-price,.array-last-chance__product-price,.dh-price,.gt-price,.su-price,.kb2-price,.november-hub__product-link>span:last-child,.array-last-chance__product-link>span:last-child')?.textContent.trim() || '' };
  }

  sync() {
    if (this.refs.wishCount.textContent !== String(this.items.length)) this.refs.wishCount.textContent = String(this.items.length);
    this.refs.wishCount.hidden = !this.items.length;
    document.querySelectorAll('[data-array-wishlist-count]').forEach(badge => {
      if (badge.textContent !== String(this.items.length)) badge.textContent = String(this.items.length);
      badge.hidden = !this.items.length;
    });
    for (const [card, button] of this.cards) {
      const record = this.recordFor(card);
      if (!record) continue;
      const saved = this.items.some(item => item.handle === record.handle);
      const action = saved ? this.labels.remove : this.labels.save;
      const pressed = String(saved);
      if (button.getAttribute('aria-pressed') !== pressed) button.setAttribute('aria-pressed', pressed);
      const label = `${action}: ${record.title}`;
      if (button.getAttribute('aria-label') !== label) button.setAttribute('aria-label', label);
      const span = button.querySelector('span');
      if (span && span.textContent !== action) span.textContent = action;
    }
  }

  handleSave = event => {
    if (event.target.closest?.('[data-array-wishlist]')) {
      event.preventDefault();
      this.openWishlist();
      return;
    }
    const button = event.target.closest?.('.array-wish-save,[data-wish-remove]');
    if (!button) return;
    if (button.hasAttribute('data-wish-remove') && !this.contains(button)) return;
    const card = [...this.cards].find(([, value]) => value === button)?.[0];
    const record = card ? this.recordFor(card) : this.items.find(item => item.handle === button.dataset.wishRemove);
    if (!record) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const saved = this.items.some(item => item.handle === record.handle);
    const next = saved ? this.items.filter(item => item.handle !== record.handle) : [...this.items, record].slice(-100);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch {
      this.refs.status.textContent = this.labels.storage_error;
      return;
    }
    this.items = next;
    this.refs.status.textContent = saved ? this.labels.removed : this.labels.saved;
    this.sync();
    if (this.refs.dialog.open) {
      this.renderList();
      const focus = this.refs.list.querySelector('button') || this.refs.dialog.querySelector('[autofocus]');
      focus?.focus();
    }
  };

  renderList() {
    const fragment = document.createDocumentFragment();
    for (const item of this.items) {
      const row = document.createElement('div');
      row.className = 'array-wishlist__item';
      const image = document.createElement('img');
      try {
        const imageURL = new URL(item.image, location.origin);
        if (['https:', 'http:'].includes(imageURL.protocol)) image.src = imageURL.href;
      } catch { /* Missing saved image: the product link remains available. */ }
      image.alt = '';
      image.width = 72;
      image.height = 88;
      image.loading = 'lazy';
      const details = document.createElement('div');
      const link = document.createElement('a');
      link.href = `${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(item.handle)}`;
      try {
        const savedURL = new URL(item.url, location.origin);
        if (savedURL.origin === location.origin && savedURL.pathname.endsWith(`/products/${item.handle}`)) link.href = savedURL.pathname + savedURL.search;
      } catch { /* Use the canonical product link if the saved URL is invalid. */ }
      link.textContent = item.title;
      details.append(link);
      if (item.priceText) {
        const price = document.createElement('p');
        price.textContent = item.priceText;
        details.append(price);
      }
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'array-wishlist__remove';
      remove.dataset.wishRemove = item.handle;
      remove.setAttribute('aria-label', `${this.labels.remove}: ${item.title}`);
      remove.append(this.refs.heart.content.cloneNode(true));
      row.append(image, details, remove);
      fragment.append(row);
    }
    this.refs.list.replaceChildren(fragment);
    this.refs.empty.hidden = Boolean(this.items.length);
  }

  openWishlist() {
    this.renderList();
    this.showDialog();
  }

  openShop(event) {
    const summary = document.querySelector('header-drawer summary');
    if (!summary) return;
    event.preventDefault();
    summary.click();
  }

  openCart(event) {
    const drawer = document.querySelector('cart-drawer-component');
    if (typeof drawer?.open !== 'function') return;
    event.preventDefault();
    drawer.open();
  }

  openAccount(event) {
    const drawer = document.querySelector('.account-drawer');
    if (typeof drawer?.showDialog !== 'function') return;
    event.preventDefault();
    drawer.showDialog();
  }

  openSearch(event) {
    const dialog = document.querySelector('#search-modal');
    if (typeof dialog?.showDialog !== 'function') return;
    event.preventDefault();
    dialog.showDialog();
  }

  updateCart = async () => {
    this.cartRequest?.abort();
    this.cartRequest = new AbortController();
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { signal: this.cartRequest.signal });
      if (!response.ok) return;
      const cart = await response.json();
      this.refs.cartCount.textContent = String(cart.item_count);
      this.refs.cartCount.hidden = !cart.item_count;
    } catch { /* Keep the last confirmed cart count on network failure. */ }
  };
}

if (!customElements.get('array-mobile-nav')) customElements.define('array-mobile-nav', ArrayMobileNav);
